import {
  Button,
  FormControl,
  FormHelperText,
  Input,
  Typography,
  Grid,
  Card,
  CircularProgress,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Controller,
  useFormContext,
  UseFormSetValue,
  UseFormSetError,
  UseFormClearErrors,
  UseFormReturn,
} from 'react-hook-form';
import ControlledTextField from './ControlledTextField.component';
import ControlledSelectField from './ControlledSelectField.component';
import {
  TDocumentStatus,
  TDocument,
  TDocumentOwnerType,
} from '../../../apis/documents/documents.interfaces';
import { TKeyValue } from '../../../utils/commonInterfaces';
import { Add, CheckCircle, CloudUpload, Refresh } from '@mui/icons-material';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';
import { useDocumentService } from 'src/apis/documents/useDocumentService';

const FileUploadErrorFallback: FC = () => {
  const { translateText } = useTranslation();
  return (
    <FormControl fullWidth error>
      <Typography variant="caption" color="error">
        {translateText('common.fieldErrorGeneric')}
      </Typography>
    </FormControl>
  );
};

export type ControlledFileUploadFieldProps = {
  label: string;
  id: string;
  name: string;
  control?: any;
  isRequired?: boolean;
  helperText?: string;
  errorMessage?: string;
  sectionTitle?: string;
  accept?: string;
  multiple?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  // Additional TDocument fields
  titleName?: string;
  titleError?: string;
  descriptionName?: string;
  descriptionError?: string;
  verificationName?: string;
  verificationError?: string;
  statusName?: string;
  statusError?: string;
  allowedFileTypes?: string[];
  documentOwnerType?: TDocumentOwnerType;
  documentOwnerId?: string;
  documentCreatedBy?: string;
  documentInstituteId?: string;
  isVerifiable?: boolean;
  /** When the form expects a full {@link TDocument} at the parent path (e.g. `foo.documentName`), merge the API document after upload. */
  mergeUploadedDocumentIntoParent?: boolean;
  // Form methods for updating form state
  setValue?: UseFormSetValue<any>;
  setError?: UseFormSetError<any>;
  clearErrors?: UseFormClearErrors<any>;
  // Upload progress callback
  onUploadProgress?: (isUploading: boolean, message: string) => void;
};

type TUploadedSnapshot = {
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  title: string;
  description: string;
  verificationDate: string;
  status: string;
};

const getStatusOptions = (
  translateText: TTranslateTextType
): TKeyValue<string>[] => [
  {
    key: TDocumentStatus.SUBMITTED,
    value: translateText('documents.submitted'),
  },
  { key: TDocumentStatus.PENDING, value: translateText('documents.pending') },
  { key: TDocumentStatus.VERIFIED, value: translateText('documents.verified') },
  { key: TDocumentStatus.REJECTED, value: translateText('documents.rejected') },
];

const getDateTimeValue = (dateValue: string) =>
  dateValue ? new Date(`${dateValue}T00:00:00.000Z`).toISOString() : undefined;

/** Stable upload payload: clone blob so clearing the <input> cannot strand an empty multipart part. */
const toUploadableFile = (file: File) =>
  new File([file], file.name, {
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
  });

const defaultTitleFromFileName = (fileName: string) => {
  const base = fileName.replace(/\.[^/.]+$/, '').trim();
  return base || fileName;
};

// Inner component that uses form context
const ControlledFileUploadFieldInner: FC<
  ControlledFileUploadFieldProps & { formContext?: UseFormReturn<any> }
> = (
  props: ControlledFileUploadFieldProps & { formContext?: UseFormReturn<any> }
) => {
  const { translateText } = useTranslation();
  const { uploadFile } = useDocumentService();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedSnapshot, setUploadedSnapshot] =
    useState<TUploadedSnapshot | null>(null);

  const formContext = props.formContext;
  const formSetValue = props.setValue || formContext?.setValue;
  const formSetError = props.setError || formContext?.setError;
  const formClearErrors = props.clearErrors || formContext?.clearErrors;

  const {
    label,
    id,
    name,
    control,
    isRequired = false,
    helperText = '',
    errorMessage,
    sectionTitle,
    accept,
    multiple = false,
    variant = 'contained',
    titleName,
    titleError,
    descriptionName,
    descriptionError,
    verificationName,
    verificationError,
    statusName,
    statusError,
    allowedFileTypes = [],
    documentOwnerType,
    documentOwnerId,
    documentCreatedBy,
    documentInstituteId,
    isVerifiable = true,
    mergeUploadedDocumentIntoParent = false,
    onUploadProgress,
  } = props;

  // Derive parent path from name prop (e.g., "transferCertificate.documentName" -> "transferCertificate")
  const getParentPath = (fieldName: string) => {
    const lastDotIndex = fieldName.lastIndexOf('.');
    return lastDotIndex > 0 ? fieldName.substring(0, lastDotIndex) : fieldName;
  };

  const parentPath = getParentPath(name);
  const resolvedTitleName = titleName ?? `${parentPath}.title`;
  const resolvedDescriptionName =
    descriptionName ?? `${parentPath}.description`;
  const resolvedVerificationName =
    verificationName ?? `${parentPath}.verificationDate`;
  const resolvedStatusName = statusName ?? `${parentPath}.status`;

  useEffect(() => {
    const currentVerificationDate = formContext?.getValues(
      resolvedVerificationName
    );

    if (!currentVerificationDate && formSetValue) {
      formSetValue(
        resolvedVerificationName,
        new Date().toISOString().slice(0, 10),
        {
          shouldValidate: false,
        }
      );
    }
  }, [formContext, formSetValue, resolvedVerificationName]);

  const watchedTitle = formContext?.watch(resolvedTitleName);
  const watchedDescription = formContext?.watch(resolvedDescriptionName);
  const watchedVerificationDate = formContext?.watch(resolvedVerificationName);
  const watchedStatus = formContext?.watch(resolvedStatusName);
  const currentUploadSnapshot: TUploadedSnapshot | null = selectedFile
    ? {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        fileLastModified: selectedFile.lastModified,
        title: String(watchedTitle ?? '').trim(),
        description: String(watchedDescription ?? '').trim(),
        verificationDate: String(watchedVerificationDate ?? ''),
        status: String(watchedStatus ?? ''),
      }
    : null;
  const isCurrentUploadSynced = Boolean(
    uploadedSnapshot &&
      currentUploadSnapshot &&
      uploadedSnapshot.fileName === currentUploadSnapshot.fileName &&
      uploadedSnapshot.fileSize === currentUploadSnapshot.fileSize &&
      uploadedSnapshot.fileLastModified ===
        currentUploadSnapshot.fileLastModified &&
      uploadedSnapshot.title === currentUploadSnapshot.title &&
      uploadedSnapshot.description === currentUploadSnapshot.description &&
      uploadedSnapshot.verificationDate ===
        currentUploadSnapshot.verificationDate &&
      uploadedSnapshot.status === currentUploadSnapshot.status
  );
  const canUploadSelectedFile = Boolean(
    selectedFile &&
      String(watchedTitle ?? '').trim() &&
      watchedStatus &&
      !isCurrentUploadSynced
  );

  const getRequiredFieldMessage = () =>
    translateText('common.fieldRequired') || 'This field is required';

  const validateUploadPrerequisites = (file: File | null) => {
    const titleValue = String(
      formContext?.getValues(resolvedTitleName) ?? ''
    ).trim();
    const statusValue = formContext?.getValues(resolvedStatusName);
    let isValid = true;

    if (!file) {
      setUploadError(translateText('common.selectFile') || 'Select a file');
      isValid = false;
    } else if (file.size === 0) {
      setUploadError(
        translateText('common.fileEmpty') || 'Selected file is empty'
      );
      isValid = false;
    }

    if (!titleValue && formSetError) {
      formSetError(resolvedTitleName, {
        type: 'manual',
        message: getRequiredFieldMessage(),
      });
      isValid = false;
    }

    if (!statusValue && formSetError) {
      formSetError(resolvedStatusName, {
        type: 'manual',
        message: getRequiredFieldMessage(),
      });
      isValid = false;
    }

    return isValid;
  };

  const handleFileUpload = async (
    file: File | null,
    fieldOnChange: (value: any) => void
  ) => {
    if (!validateUploadPrerequisites(file)) {
      return;
    }

    const titleValue = String(
      formContext?.getValues(resolvedTitleName) ?? ''
    ).trim();
    const descriptionValue = String(
      formContext?.getValues(resolvedDescriptionName) ?? ''
    ).trim();
    const verificationDateValue = String(
      formContext?.getValues(resolvedVerificationName) ?? ''
    );
    const statusValue = formContext?.getValues(resolvedStatusName);

    setIsUploading(true);
    setUploadError(null);
    onUploadProgress?.(
      true,
      translateText('common.uploading') || 'Uploading...'
    );

    try {
      const fileForUpload = toUploadableFile(file as File);
      const document: TDocument = await uploadFile(fileForUpload, {
        name: titleValue,
        description: descriptionValue || titleValue,
        ownerType: documentOwnerType,
        ownerId: documentOwnerId,
        createdBy: documentCreatedBy,
        instituteId: documentInstituteId,
        verificationDate: getDateTimeValue(verificationDateValue),
        isVerifiable,
      });

      // Update form fields with document data
      if (formSetValue) {
        fieldOnChange(document.documentName);
        formSetValue(name, document.documentName, { shouldValidate: true });
        formSetValue(resolvedTitleName, titleValue, {
          shouldValidate: true,
        });
        formSetValue(resolvedDescriptionName, descriptionValue || '', {
          shouldValidate: false,
        });
        formSetValue(
          resolvedVerificationName,
          document.verificationDate || verificationDateValue,
          { shouldValidate: false }
        );
        formSetValue(resolvedStatusName, statusValue, {
          shouldValidate: true,
        });

        if (mergeUploadedDocumentIntoParent && formContext) {
          const prev = formContext.getValues(parentPath);
          const safePrev =
            prev !== null &&
            prev !== undefined &&
            typeof prev === 'object' &&
            !Array.isArray(prev)
              ? { ...(prev as Record<string, unknown>) }
              : {};
          const documentSansUndefined = Object.fromEntries(
            Object.entries(document).filter(([, v]) => v !== undefined)
          ) as Partial<TDocument>;
          formSetValue(
            parentPath,
            { ...safePrev, ...documentSansUndefined } as TDocument &
              Record<string, unknown>,
            { shouldValidate: true }
          );
        }
      } else {
        // Fallback: just update the field value
        fieldOnChange(document.documentName);
      }

      // Clear any previous errors
      if (formClearErrors) {
        formClearErrors(name);
        formClearErrors(resolvedTitleName);
        formClearErrors(resolvedDescriptionName);
        formClearErrors(resolvedVerificationName);
        formClearErrors(resolvedStatusName);
      }

      onUploadProgress?.(false, '');
      setUploadedSnapshot({
        fileName: file?.name ?? '',
        fileSize: file?.size ?? 0,
        fileLastModified: file?.lastModified ?? 0,
        title: titleValue,
        description: descriptionValue,
        verificationDate: document.verificationDate || verificationDateValue,
        status: String(statusValue ?? ''),
      });
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        translateText('common.uploadError') ||
        'Failed to upload file';
      setUploadError(errorMessage);

      // Set errors on form fields
      if (formSetError) {
        formSetError(name, {
          type: 'manual',
          message: errorMessage,
        });
        if (resolvedTitleName) {
          formSetError(resolvedTitleName, {
            type: 'manual',
            message: errorMessage,
          });
        }
        if (resolvedDescriptionName) {
          formSetError(resolvedDescriptionName, {
            type: 'manual',
            message: errorMessage,
          });
        }
        if (resolvedVerificationName) {
          formSetError(resolvedVerificationName, {
            type: 'manual',
            message: errorMessage,
          });
        }
        if (resolvedStatusName) {
          formSetError(resolvedStatusName, {
            type: 'manual',
            message: errorMessage,
          });
        }
      }
      onUploadProgress?.(false, errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <ErrorBoundary
      FallbackComponent={FileUploadErrorFallback}
      onError={(error, errorInfo) => {
        console.error(
          'ControlledFileUploadField error:',
          error,
          errorInfo,
          name,
          id
        );
      }}
    >
      <Card
        sx={{
          padding: {
            xs: 1.5,
            sm: 2,
          },
        }}
        data-testid="file-upload-field-card"
      >
        <Grid
          container
          spacing={{
            xs: 1.5,
            sm: 2,
          }}
          data-testid="file-upload-field-container"
        >
          <Grid item xs={12}>
            {sectionTitle ? (
              <Grid item xs={12} data-testid="file-upload-field-section-title">
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: {
                      xs: '0.875rem',
                      sm: '1rem',
                    },
                  }}
                >
                  {sectionTitle}
                </Typography>
              </Grid>
            ) : null}
            <Grid
              container
              spacing={{
                xs: 1.5,
                sm: 2,
              }}
              data-testid="file-upload-field-content"
            >
              {/* First Row: File Upload, Title, Verification Date, Status, Upload */}
              {control ? (
                <Controller
                  name={name}
                  control={control}
                  render={({ field }) => {
                    const handleFileChange = async (
                      event: React.ChangeEvent<HTMLInputElement>
                    ) => {
                      const files = event.target.files;
                      if (files && files.length > 0) {
                        const file = files[0];
                        if (multiple) {
                          // For multiple files, just set the name
                          setSelectedFile(null);
                          field.onChange(
                            Array.from(files)
                              .map((f: File) => f.name)
                              .join(', ')
                          );
                        } else {
                          const titleCurrent = String(
                            formContext?.getValues(resolvedTitleName) ?? ''
                          ).trim();
                          const descCurrent = String(
                            formContext?.getValues(resolvedDescriptionName) ??
                              ''
                          ).trim();
                          const fallbackTitle = defaultTitleFromFileName(
                            file.name
                          );
                          if (formSetValue && !titleCurrent) {
                            formSetValue(resolvedTitleName, fallbackTitle, {
                              shouldValidate: true,
                            });
                          }
                          if (formSetValue && !descCurrent) {
                            formSetValue(
                              resolvedDescriptionName,
                              fallbackTitle,
                              {
                                shouldValidate: false,
                              }
                            );
                          }
                          setSelectedFile(file);
                          setUploadError(null);
                          field.onChange(null);
                          formClearErrors?.(name);
                        }
                      } else {
                        setSelectedFile(null);
                        field.onChange(null);
                      }
                      event.target.value = '';
                    };

                    const getFileName = () => {
                      if (selectedFile) return selectedFile.name;
                      if (!field.value) return '';
                      return typeof field.value === 'string' ? field.value : '';
                    };

                    return (
                      <>
                        <Grid item xs={12} sm={6} md={2}>
                          <FormControl
                            fullWidth
                            required={isRequired}
                            error={!!errorMessage || !!uploadError}
                            data-testid={`file-upload-control-${id}`}
                          >
                            <Button
                              component="label"
                              role={undefined}
                              variant={'outlined'}
                              tabIndex={-1}
                              fullWidth
                              disabled={isUploading}
                              data-testid={`file-upload-button-${id}`}
                              sx={{
                                minHeight: {
                                  xs: '44px',
                                  sm: '36px',
                                },
                                fontSize: {
                                  xs: '16px', // Prevents zoom on iOS
                                  sm: '14px',
                                },
                                padding: {
                                  xs: '12px 16px',
                                  sm: '8px 16px',
                                },
                              }}
                              startIcon={getFileName() ? <Refresh /> : <Add />}
                            >
                              {getFileName()
                                ? translateText('common.replace')
                                : translateText('common.select')}
                              <Input
                                type="file"
                                id={id}
                                name={field.name}
                                inputProps={{
                                  ...(allowedFileTypes.length > 0
                                    ? { accept: allowedFileTypes.join(',') }
                                    : {}),
                                  multiple,
                                }}
                                onChange={handleFileChange}
                                onBlur={field.onBlur}
                                sx={{
                                  clip: 'rect(0 0 0 0)',
                                  clipPath: 'inset(50%)',
                                  height: 1,
                                  overflow: 'hidden',
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  whiteSpace: 'nowrap',
                                  width: 1,
                                }}
                              />
                            </Button>
                            {getFileName() && (
                              <Typography variant="caption" sx={{ mt: 1 }}>
                                {getFileName()}
                              </Typography>
                            )}
                            {(errorMessage || uploadError) && (
                              <FormHelperText error>
                                {errorMessage || uploadError}
                              </FormHelperText>
                            )}
                            {helperText && !errorMessage && !uploadError && (
                              <FormHelperText>{helperText}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                          <ControlledTextField
                            control={control}
                            name={resolvedTitleName}
                            id={`${id}-title`}
                            label={translateText('common.title')}
                            errorMessage={titleError}
                            isGridItem={false}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                          <ControlledTextField
                            control={control}
                            name={resolvedVerificationName}
                            id={`${id}-verification`}
                            label={translateText('common.verificationDate')}
                            errorMessage={verificationError}
                            isGridItem={false}
                            type="date"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={2}
                          data-testid="status-select-field-container"
                        >
                          <ControlledSelectField
                            control={control}
                            name={resolvedStatusName}
                            id={`${id}-status`}
                            label={translateText('common.status')}
                            itemList={getStatusOptions(translateText)}
                            errorMessage={statusError}
                            isRequired
                          />
                        </Grid>
                        {!multiple ? (
                          <Grid item xs={12} sm={6} md={3}>
                            <Button
                              variant="contained"
                              color={
                                isCurrentUploadSynced ? 'success' : 'primary'
                              }
                              fullWidth
                              disabled={
                                isUploading ||
                                isCurrentUploadSynced ||
                                !canUploadSelectedFile
                              }
                              data-testid={`file-upload-submit-button-${id}`}
                              sx={(theme) => ({
                                minHeight: {
                                  xs: '44px',
                                  sm: '36px',
                                },
                                fontSize: {
                                  xs: '16px',
                                  sm: '14px',
                                },
                                padding: {
                                  xs: '12px 16px',
                                  sm: '8px 16px',
                                },
                                ...(isCurrentUploadSynced
                                  ? {
                                      '&.Mui-disabled': {
                                        backgroundColor:
                                          theme.palette.success.main,
                                        color:
                                          theme.palette.success.contrastText,
                                      },
                                    }
                                  : {}),
                              })}
                              startIcon={
                                isUploading ? (
                                  <CircularProgress size={20} />
                                ) : isCurrentUploadSynced ? (
                                  <CheckCircle />
                                ) : (
                                  <CloudUpload />
                                )
                              }
                              onClick={() =>
                                handleFileUpload(selectedFile, field.onChange)
                              }
                            >
                              {isUploading
                                ? translateText('common.uploading') ||
                                  'Uploading...'
                                : isCurrentUploadSynced
                                  ? translateText('common.uploaded') ||
                                    'Uploaded'
                                  : translateText('common.uploadFile')}
                            </Button>
                          </Grid>
                        ) : null}
                      </>
                    );
                  }}
                />
              ) : (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl
                    fullWidth
                    required={isRequired}
                    error={!!errorMessage}
                    data-testid={`file-upload-control-${id}`}
                  >
                    <Button
                      component="label"
                      role={undefined}
                      variant={variant}
                      tabIndex={-1}
                      fullWidth
                      data-testid={`file-upload-button-${id}`}
                      sx={{
                        minHeight: {
                          xs: '44px',
                          sm: '36px',
                        },
                        fontSize: {
                          xs: '16px', // Prevents zoom on iOS
                          sm: '14px',
                        },
                        padding: {
                          xs: '12px 16px',
                          sm: '8px 16px',
                        },
                      }}
                    >
                      {label}
                      <Input
                        type="file"
                        id={id}
                        name={name}
                        inputProps={{ accept, multiple }}
                        sx={{
                          clip: 'rect(0 0 0 0)',
                          clipPath: 'inset(50%)',
                          height: 1,
                          overflow: 'hidden',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          whiteSpace: 'nowrap',
                          width: 1,
                        }}
                      />
                    </Button>
                    {errorMessage && (
                      <FormHelperText error>{errorMessage}</FormHelperText>
                    )}
                    {helperText && !errorMessage && (
                      <FormHelperText>{helperText}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Grid>
          {/* Second Row: Description (full width) */}
          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              name={resolvedDescriptionName}
              id={`${id}-description`}
              label={translateText('common.description')}
              errorMessage={descriptionError}
              isGridItem={false}
              multiline
              rows={3}
              fullWidth
            />
          </Grid>
        </Grid>
      </Card>
    </ErrorBoundary>
  );
};

// Component that uses form context - requires FormProvider
const ControlledFileUploadField: FC<ControlledFileUploadFieldProps> = (
  props: ControlledFileUploadFieldProps
) => {
  const formContext = useFormContext();
  return (
    <ControlledFileUploadFieldInner {...props} formContext={formContext} />
  );
};

export default ControlledFileUploadField;
